<template>
  <section>
    <AlertBanner v-if="hasPhotoRejected" id="submit-error-alert" :status="0" class="mt-0 sm:mt-32 lg:mt-0" style="width: 100%">
      <div>
        Your profile photo did not meet our
        <a
          target="_blank"
          href="https://support.sittercity.com/hc/en-us/articles/115012280247-Profile-photo-guidelines-for-families"
          class="primary-link"
          >Photo Guidelines</a
        >. Check the guidelines and <a href="/account/seekers/photo#/upload" class="primary-link">upload a new photo</a>.
      </div>
    </AlertBanner>
    <DashboardLoadingSkeleton v-if="isLoading || outOfNetworkLoading" />
    <div v-else class="dashboard flex flex-col lg:flex-row lg:justify-center lg:gap-32">
      <GenericError v-if="hasError" />
      <section v-else class="dashboard-main px-16 lg:px-0">
        <DashboardHeading v-if="showHeader" v-once :account="account" :dashboard-data="dashboardData" />
        <OutOfNetworkCareModule :reservation-requests="reservationRequests" />
        <getting-started :account="account" />

        <div v-if="hasActiveOrPendingJobs">
          <h2 class="text-h1 mb-16">Jobs</h2>
          <JobsModule v-once :jobs="jobs" :eligible-jobs="eligibleJobs" :account="account" />
          <PremiumUpgradeModule v-if="basicAccount" v-once />
          <h1 v-if="showExplore" class="text-h1 mb-16">Explore Sittercity</h1>
          <NearbySittersList v-if="sitters.length > 0" :sitters="sitters" :zipcode="zipcode" :city="city" />
          <TrustSafetyModule :jobs="activeJobs" :account-type="account.accountType()" />
          <NearbyAdventuresList v-if="adventures.length > 0" :adventures="adventures" :zipcode="zipcode" :city="city" />
          <categorized-partners-list v-if="!basicAccount" :zipCode="zipcode" />
        </div>

        <!-- no active or pending jobs -->
        <div v-else>
          <h2 class="text-h1 mb-16">{{ requireJobPost || !hasJobHistory ? 'Start here: let’s create a job' : 'Jobs' }}</h2>

          <h3 v-if="!requireJobPost && hasJobHistory" class="text-h4 mb-4">Start your job post here:</h3>

          <p v-if="hasJobHistory" class="body-1 mb-16">
            Skip the scrolling and searching. Share how you need help and matching caregivers will reach out to you.
          </p>
          <p v-else class="body-1 mb-16">Share how you need help and caregivers will apply to introduce themselves.</p>

          <JobPostCarousel v-once :includeHousekeeping="account.isCorporate()" />

          <PremiumUpgradeModule v-if="basicAccount" v-once />
          <h1 v-if="showExplore && !requireJobPost" class="text-h1 mb-16">Explore Sittercity</h1>
          <NearbySittersList v-if="sitters.length > 0" :sitters="sitters" :zipcode="zipcode" :city="city" />
          <TrustSafetyModule :jobs="activeJobs" :account-type="account.accountType()" />
          <NearbyAdventuresList
            v-if="adventures.length > 0 && !requireJobPost"
            :adventures="adventures"
            :zipcode="zipcode"
            :city="city"
          />
          <categorized-partners-list v-if="!requireJobPost && !basicAccount" :zipCode="zipcode" />
        </div>
      </section>
    </div>
    <BenefitsCallout v-if="!isLoading && !outOfNetworkLoading && showBHCallout" :corpSlug="dashboardData.corpUserGroupInfo.url" />
  </section>
</template>

<script lang="ts">
// Global Components
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import CategorizedPartnersList from '@/components/bh_partners/CategorizedPartnersList.vue';

// Dashboard Components
import BenefitsCallout from './BenefitsCallout.vue';
import GenericError from '@/components/generic_error/GenericError.vue';
import DashboardHeading from './DashboardHeading.vue';
import DashboardLoadingSkeleton from './DashboardLoadingSkeleton.vue';
import JobsModule from './JobsModule.vue';
import JobPostCarousel from './JobPostCarousel.vue';
import NearbyAdventuresList from './NearbyAdventuresList.vue';
import NearbySittersList from './NearbySittersList.vue';
import OutOfNetworkCareModule from './OutOfNetworkCareModule.vue';
import PremiumUpgradeModule from './PremiumUpgradeModule.vue';
import TrustSafetyModule from './TrustSafetyModule.vue';
import GettingStarted from './components/GettingStarted.vue';

// GraphQL
import { FETCH_SEEKER_DASHBOARD_DATA_QUERY, FETCH_ADVENTURE_DATA } from '@/graphql/account/queries';
import { BH_RESERVATION_REQUESTS } from '@/graphql/backup_care/queries';

// Lib
import { readMetaBoolean, shouldShowSMSModal } from '../../../lib/page';
import { sendBrazeEvent } from '../../../lib/braze';
import { sendSegmentEvent } from '../../../lib/segment';
import { setPhotoRejectCookie } from '@/lib/domain_cookie';
import { PARENT_DASHBOARD_LOADED, SHOW_PARENT_SMS_MODAL } from '@/lib/constants/braze_custom_events';
import JobPresenter from '@/lib/presenters/job_presenter';

export default {
  name: 'Dashboard',
  components: {
    // Global Components
    AlertBanner,
    CategorizedPartnersList,

    // Dashboard Components
    BenefitsCallout,
    GenericError,
    DashboardHeading,
    DashboardLoadingSkeleton,
    JobsModule,
    JobPostCarousel,
    NearbyAdventuresList,
    NearbySittersList,
    OutOfNetworkCareModule,
    PremiumUpgradeModule,
    TrustSafetyModule,
    GettingStarted,
  },
  props: {
    hasPhotoRejectedProd: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      zipcode: '',
      city: '',
      dashboardData: null,
      adventures: [],
      sitters: [],
      reservationRequests: [],
      hasError: false,
      isLoading: true,
      outOfNetworkLoading: true,
      hasPhotoRejected: this.hasPhotoRejectedProd,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    hasJobHistory(): boolean {
      return this.dashboardData.jobs.edges.length !== 0;
    },
    showHeader(): boolean {
      return !this.account.isBasic() || this.hasJobHistory;
    },
    requireJobPost() {
      return readMetaBoolean('require-job-post');
    },
    jobs(): Array<any> {
      return this.dashboardData.jobs.edges;
    },
    eligibleJobs(): Array<any> {
      return this.parseJobs(this.jobs);
    },
    activeJobs(): Array<any> {
      return this.jobs.filter((job: any) => job.node.isActive);
    },
    pendingJobs(): Array<any> {
      return this.jobs.filter((job: any) => job.node.status === 'PENDING');
    },
    hasApplications(): boolean {
      if (!this.activeJobs.length) return false;
      return this.activeJobs.some((job) => {
        const applications = job.node.applications.edges;
        return applications.filter((app) => app.node.status === 'open');
      });
    },
    hasActiveJobs(): boolean {
      return this.activeJobs.length > 0;
    },
    hasActiveOrPendingJobs(): boolean {
      return this.hasActiveJobs || this.pendingJobs.length > 0;
    },
    basicAccount(): boolean {
      return !(this.account.isPremium() || this.isCorporate);
    },
    showExplore(): boolean {
      return this.adventures.length > 0 || this.sitters.length > 0;
    },
    showBHCallout(): boolean {
      return this.dashboardData?.excludedFromBhList === false;
    },
    isCorporate() {
      return this.account.isCorporate();
    },
  },
  mounted() {
    sendBrazeEvent(PARENT_DASHBOARD_LOADED);

    if (this.shouldShowSMSModal()) {
      sendBrazeEvent(SHOW_PARENT_SMS_MODAL);
    }

    this.hasPhotoRejected = setPhotoRejectCookie(
      this.account.profilePhotoStatus,
      `profilePhotoAlert-${this.account.id}`,
      'True',
      10
    );
    this.timeout = setTimeout(this.clearAlertBanner, 15000);
  },
  apollo: {
    sitterSearch: {
      query: FETCH_SEEKER_DASHBOARD_DATA_QUERY,
      variables(): any {
        return {
          geoPointX: this.account.location.longitude.toString(),
          geoPointY: this.account.location.latitude.toString(),
        };
      },
      result({ data }) {
        this.checkForFailedQuery(data);

        this.dashboardData = data.viewer;
        this.sitters = data.sitterSearch.edges;
        this.isLoading = false;
      },
      error(err) {
        this.hasError = true;
        // @ts-ignore
        // eslint-disable-next-line no-console
        console.error(err);
      },
    },
    // Returning on error since adventureSearch query does not work on prod yet
    // TODO: Add error handling once adventures are available in production
    adventureSearch: {
      query: FETCH_ADVENTURE_DATA,
      variables(): any {
        return {
          longitude: this.account.location.longitude.toString(),
          latitude: this.account.location.latitude.toString(),
        };
      },
      result({ data }) {
        if (!data) return;

        this.adventures = data.adventureSearch.edges;
      },
      error(err) {
        // @ts-ignore
        // eslint-disable-next-line no-console
        console.error(err);
      },
    },
    corp: {
      query: BH_RESERVATION_REQUESTS,
      skip(): boolean {
        return !this.isCorporate;
      },
      result({ data }) {
        if (!data) {
          this.outOfNetworkLoading = false;
          return;
        }

        const requests = data.corp?.backupCare?.bhReservationRequests ?? [];
        this.reservationRequests = requests.filter((reservations) => reservations.scCareSessions?.length);
        this.outOfNetworkLoading = false;
      },
      error(err) {
        this.outOfNetworkLoading = false;
        // @ts-ignore
        // eslint-disable-next-line no-console
        console.error(err);
      },
    },
  },
  created() {
    // when creating jobs on sitterctiy, we are redirected here with a posted_job_care_type url params
    // if that is present; send a job posted segment event
    const jobFamilyType = new URLSearchParams(window.location.search).get('posted_job_family_type');
    if (jobFamilyType) {
      this.sendJobPostedClient(jobFamilyType);

      // remove the params we just used so as to prevent the segment event from being called again
      const urlWithoutParams = window.location.origin + window.location.pathname;
      window.history.replaceState(null, '', urlWithoutParams);
    }

    // Set location data from account
    if (this.account && this.account.location) {
      this.zipcode = this.account.location.zipcode;
      this.city = this.account.location.cityname;
    }
    if (!this.account || !this.isCorporate) {
      this.outOfNetworkLoading = false;
    }
  },
  methods: {
    /**
     * Determines if the SMS modal should be shown based on account creation date.
     * Delegates to the shared utility function in lib/page.ts.
     */
    shouldShowSMSModal(): boolean {
      return shouldShowSMSModal(this.account?.createdAt);
    },
    checkForFailedQuery(data: any) {
      if (data) return;

      this.hasError = true;
      this.isLoading = false;
    },

    parseJobs(jobs: any[] = []) {
      // active or pending job edges
      const jobNodes = jobs.map(({ node }) => node).filter((node) => node.isActive || node.status === 'PENDING');
      // inactive, eligible job edges
      const inactiveEligible = jobNodes.filter((node: any) => !node.isActive && new JobPresenter(node).isEligible());
      // return first 2 jobs
      return jobNodes.concat(inactiveEligible).slice(0, 2);
    },
    clearAlertBanner() {
      this.hasPhotoRejected = false;
    },
    sendJobPostedClient(jobFamilyType: string) {
      sendSegmentEvent('Job Posted - Client', {
        family_type: jobFamilyType,
      });
    },
  },
};
</script>
